/**
 * Sale Invoice mapping — Access `frmReportCashFlowQryBanHang_add`.
 *
 * Pick an ERP sale invoice + a MISA invoice; Save writes the link into
 * `report_cashflow_map_sohoadon_banhang` + upserts the extract join row keyed
 * on misa_ref. Contract/Invoice extract are the only editable text fields.
 */
import { Autocomplete, Box, Dialog, DialogContent, TextField } from '@mui/material'
import DialogTitleBar from '../../../../components/DialogTitleBar'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetCashflowDetailLookupQuery,
  useSaveCashflowMapBanHangMutation,
  type MapBanHang,
} from '../../../../store/api/reportCashflowApi'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { FORM_GRID_2COL } from '../../../../theme/responsive'

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  row: MapBanHang | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

export default function BanHangMapDialog({ open, mode, row, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [erpInput, setErpInput] = useState('')
  const [misaInput, setMisaInput] = useState('')
  const [erp, setErp] = useState<Row | null>(null)
  const [misa, setMisa] = useState<Row | null>(null)
  const [contractExtract, setContractExtract] = useState('')
  const [invExtract, setInvExtract] = useState('')
  const [save, { isLoading }] = useSaveCashflowMapBanHangMutation()

  const { data: erpData } = useGetCashflowDetailLookupQuery({ kind: 'erp_banhang', q: erpInput || undefined }, { skip: !open })
  const { data: misaData } = useGetCashflowDetailLookupQuery({ kind: 'misa_banhang', q: misaInput || undefined }, { skip: !open })

  const erpLabel = (r: Row) => s(r.filter_txt) || `${s(r.contract_no)} - ${s(r.erp_inv)}`
  const misaLabel = (r: Row) => s(r.filter_txt) || `${s(r.misa_sohoadon)} - ${s(r.sochungtu)}`

  const erpOptions = useMemo(() => erpData?.items ?? [], [erpData])
  const misaOptions = useMemo(() => misaData?.items ?? [], [misaData])
  const erpMerged = useMemo(
    () => (erp && !erpOptions.some((o) => erpLabel(o) === erpLabel(erp)) ? [erp, ...erpOptions] : erpOptions),
    [erp, erpOptions],
  )
  const misaMerged = useMemo(
    () => (misa && !misaOptions.some((o) => s(o.ref_id) === s(misa.ref_id)) ? [misa, ...misaOptions] : misaOptions),
    [misa, misaOptions],
  )

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      setErp({ contract_no: row.contractNo, erp_inv: row.erpInv, account_object_code: row.accountObjectCode, cp_code: row.cpCode, filter_txt: `${s(row.contractNo)} - ${s(row.erpInv)}` })
      setMisa({ ref_id: row.refId, misa_sohoadon: row.misaSohoadon, sochungtu: row.sochungtu, filter_txt: `${s(row.misaSohoadon)} - ${s(row.sochungtu)}` })
      setContractExtract(s(row.contractExtract))
      setInvExtract(s(row.invExtract))
    } else {
      setErp(null)
      setMisa(null)
      setContractExtract('')
      setInvExtract('')
    }
    setErpInput('')
    setMisaInput('')
  }, [open, mode, row])

  const handleSave = async () => {
    if (!canEdit) return
    if (!s(erp?.contract_no)) {
      window.alert('Please choose ERP INVOICE!')
      return
    }
    if (!row?.misaMapId && !s(misa?.ref_id)) {
      window.alert('Please choose MISA INVOICE in the INSERT query!')
      return
    }
    try {
      const res = await save({
        misaMapId: mode === 'edit' ? row?.misaMapId ?? null : null,
        erpInvNo: s(erp?.erp_inv),
        erpContractNo: s(erp?.contract_no),
        erpCpCode: s(erp?.account_object_code) || s(erp?.cp_code),
        misaCpCode: s(misa?.erp_cp_code),
        misaRefId: s(misa?.ref_id),
        contractExtract: contractExtract || null,
        invExtract: invExtract || null,
      }).unwrap()
      snack.success(res.message || 'Successfully Added')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not save the mapping.')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitleBar
        title="Sale Invoice - Misa Invoice"
        onSave={handleSave}
        onCancel={onClose}
        saving={isLoading}
        saveDisabled={isLoading || !canEdit}
      />
      <DialogContent>
        <Box sx={{ ...FORM_GRID_2COL, mt: 0.5 }}>
          <Autocomplete
            size="small"
            sx={{ gridColumn: '1 / -1' }}
            options={erpMerged}
            getOptionLabel={erpLabel}
            isOptionEqualToValue={(o, v) => erpLabel(o) === erpLabel(v)}
            value={erp}
            onChange={(_, v) => setErp(v)}
            inputValue={erpInput}
            onInputChange={(_, v) => setErpInput(v)}
            renderInput={(p) => <TextField {...p} label="ERP Invoice" InputLabelProps={{ shrink: true }} />}
          />
          <Autocomplete
            size="small"
            sx={{ gridColumn: '1 / -1' }}
            options={misaMerged}
            getOptionLabel={misaLabel}
            isOptionEqualToValue={(o, v) => s(o.ref_id) === s(v.ref_id)}
            value={misa}
            onChange={(_, v) => setMisa(v)}
            inputValue={misaInput}
            onInputChange={(_, v) => setMisaInput(v)}
            renderInput={(p) => <TextField {...p} label="MISA Invoice" InputLabelProps={{ shrink: true }} />}
          />
          <TextField
            label="MISA - Contract Extract"
            value={contractExtract}
            onChange={(e) => setContractExtract(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="MISA - Invoice Extract"
            value={invExtract}
            onChange={(e) => setInvExtract(e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
